import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import ShopHeader from '@/components/commerce/ShopHeader';
import { useShopCart } from '@/context/ShopCartContext';
import { useShopOrg } from '@/context/ShopOrgContext';
import { useShopCustomerAuth } from '@/context/ShopCustomerAuthContext';
import { Check, ChevronLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { step: 1, label: 'Gegevens' },
  { step: 2, label: 'Verzending' },
  { step: 3, label: 'Betalen' },
  { step: 4, label: 'Bevestiging' },
];

const EMPTY_FORM = {
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  fulfillment_method: 'afhalen',
  billing_address: '',
  shipping_address: '',
  notes: '',
};

const StepIndicator = ({ currentStep }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {STEPS.map((s, index) => (
      <React.Fragment key={s.step}>
        <div className="flex flex-col items-center gap-1">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
            currentStep > s.step ? 'bg-teal-600 text-white' : currentStep === s.step ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-400'
          }`}>
            {currentStep > s.step ? <Check className="w-4 h-4" /> : s.step}
          </div>
          <span className={`text-xs ${currentStep >= s.step ? 'text-slate-900' : 'text-slate-400'}`}>{s.label}</span>
        </div>
        {index < STEPS.length - 1 && <div className={`w-8 h-0.5 mb-4 ${currentStep > s.step ? 'bg-teal-600' : 'bg-slate-200'}`} />}
      </React.Fragment>
    ))}
  </div>
);

const ShopCheckoutPage = () => {
  const { id: orgId, slug: shopSlug, name: organizationName, logoUrl } = useShopOrg();
  const { customer, token, isLoggedIn } = useShopCustomerAuth();
  const navigate = useNavigate();
  const { cart, cartTotal, clearCart } = useShopCart();

  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(null);

  useEffect(() => {
    if (cart.length === 0 && !orderResult) {
      navigate(`/${shopSlug}/shop/products`);
    }
  }, [cart, orderResult, shopSlug, navigate]);

  useEffect(() => {
    if (isLoggedIn && customer) {
      setForm((prev) => ({
        ...prev,
        customer_name: prev.customer_name || customer.name,
        customer_email: prev.customer_email || customer.email,
        customer_phone: prev.customer_phone || customer.phone || '',
      }));
    }
  }, [isLoggedIn, customer]);

  const goNext = () => setCurrentStep((s) => Math.min(4, s + 1));
  const goBack = () => setCurrentStep((s) => Math.max(1, s - 1));

  const canProceedStep1 = form.customer_name.trim() && form.customer_email.trim();
  const canProceedStep2 = form.fulfillment_method === 'afhalen' || form.shipping_address.trim();

  const submitOrder = async () => {
    try {
      setSubmitting(true);
      const payload = {
        items: cart.map((line) => ({ product_id: line.product.id, quantity: line.quantity })),
        ...form,
      };
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
      const response = await axios.post(`${API}/public/commerce/${orgId}/orders`, payload, { headers });
      setOrderResult(response.data);
      clearCart();
      setCurrentStep(4);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Bestelling plaatsen mislukt');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />
      <ShopHeader organizationName={organizationName} logoUrl={logoUrl} showSearch={false} searchValue="" onSearchChange={() => {}} />

      <main className="max-w-2xl mx-auto px-4 py-10">
        <StepIndicator currentStep={currentStep} />

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            {currentStep === 1 && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-slate-900">Jouw gegevens</h2>
                <div>
                  <Label htmlFor="checkout-name">Naam</Label>
                  <Input id="checkout-name" required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="checkout-email">E-mail</Label>
                    <Input id="checkout-email" type="email" required value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="checkout-phone">Telefoon</Label>
                    <Input id="checkout-phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} />
                  </div>
                </div>
                <div className="flex justify-end pt-2">
                  <Button className="rounded-xl bg-teal-600 hover:bg-teal-700" disabled={!canProceedStep1} onClick={goNext}>
                    Volgende
                  </Button>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-slate-900">Verzending</h2>
                <div>
                  <Label>Ophalen of verzenden</Label>
                  <Select value={form.fulfillment_method} onValueChange={(value) => setForm({ ...form, fulfillment_method: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="afhalen">Afhalen</SelectItem>
                      <SelectItem value="verzenden">Verzenden</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.fulfillment_method === 'verzenden' && (
                  <div>
                    <Label htmlFor="checkout-shipping">Verzendadres</Label>
                    <Textarea id="checkout-shipping" required value={form.shipping_address} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })} />
                  </div>
                )}
                <div>
                  <Label htmlFor="checkout-notes">Opmerking (optioneel)</Label>
                  <Textarea id="checkout-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
                <div className="flex justify-between pt-2">
                  <Button variant="outline" className="rounded-xl" onClick={goBack}>
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Terug
                  </Button>
                  <Button className="rounded-xl bg-teal-600 hover:bg-teal-700" disabled={!canProceedStep2} onClick={goNext}>
                    Volgende
                  </Button>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-slate-900">Bestelling & betaling</h2>
                <div className="space-y-2">
                  {cart.map((line) => (
                    <div key={line.product.id} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">{line.product.title} x{line.quantity}</span>
                      <span className="text-slate-900">€ {((line.product.sale_price ?? line.product.price) * line.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-slate-100 pt-3 flex items-center justify-between">
                  <span className="text-sm text-slate-500">Totaal (incl. btw)</span>
                  <span className="text-lg font-semibold text-slate-900">€ {cartTotal.toFixed(2)}</span>
                </div>
                <div className="bg-slate-50 rounded-xl p-3 text-sm text-slate-600">
                  Betaling via bankoverschrijving of contant bij afhalen — je ontvangt de betaalinstructies per e-mail.
                </div>
                <div className="flex justify-between pt-2">
                  <Button variant="outline" className="rounded-xl" onClick={goBack}>
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Terug
                  </Button>
                  <Button className="rounded-xl bg-teal-600 hover:bg-teal-700" disabled={submitting} onClick={submitOrder}>
                    {submitting ? 'Bezig...' : 'Bestelling plaatsen'}
                  </Button>
                </div>
              </div>
            )}

            {currentStep === 4 && orderResult && (
              <div className="text-center space-y-4 py-4">
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                  <Check className="w-7 h-7 text-emerald-600" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900">Bedankt voor je bestelling!</h2>
                <p className="text-slate-500">
                  Bestelnummer <span className="font-mono font-medium text-slate-900">{orderResult.order_number}</span>
                </p>
                <p className="text-2xl font-semibold text-slate-900">€ {orderResult.total_amount.toFixed(2)}</p>
                <p className="text-sm text-slate-500">Je ontvangt een bevestiging per e-mail.</p>
                <Button className="rounded-xl bg-teal-600 hover:bg-teal-700" onClick={() => navigate(`/${shopSlug}/shop`)}>
                  Terug naar winkel
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ShopCheckoutPage;
